import { useEffect } from "react";
import { type ThemeSettings, useThemeSettings } from "@/hooks/use-theme-settings";

/** "#rrggbb" → [r, g, b], or null when malformed (defensive — API validates). */
function parseHex(hex: string): [number, number, number] | null {
  const digits = /^#([0-9a-fA-F]{6})$/.exec(hex)?.[1];
  if (!digits) return null;
  const n = Number.parseInt(digits, 16);
  return [Math.floor(n / 65536) % 256, Math.floor(n / 256) % 256, n % 256];
}

/** Readable text color (ink or white) for the given background, by YIQ luma. */
function contrastForeground(hex: string): string {
  const rgb = parseHex(hex);
  if (!rgb) return "#ffffff";
  const [r, g, b] = rgb;
  return (r * 299 + g * 587 + b * 114) / 1000 >= 145 ? "#1f2a44" : "#ffffff";
}

/** Mix `hex` toward `toward` by `amount` (0..1) — for derived hover surfaces. */
function mix(hex: string, toward: string, amount: number): string {
  const a = parseHex(hex);
  const b = parseHex(toward);
  if (!a || !b) return hex;
  const channel = (i: 0 | 1 | 2) => Math.round(a[i] + (b[i] - a[i]) * amount);
  return `#${[channel(0), channel(1), channel(2)]
    .map((c) => c.toString(16).padStart(2, "0"))
    .join("")}`;
}

/**
 * CSS for the admin-configured overrides. Only slots that are set emit rules;
 * an all-null theme emits nothing and the built-in brand theme applies. Rules
 * target both `:root` and `.dark` so the chosen brand survives the theme
 * toggle — this style element is appended after the bundled stylesheet, so it
 * wins ties.
 */
function buildOverrideCss(theme: ThemeSettings): string {
  const both: string[] = [];
  const bodyRules: string[] = [];
  const displayRules: string[] = [];

  if (theme.primaryColor) {
    const fg = contrastForeground(theme.primaryColor);
    both.push(
      `--primary: ${theme.primaryColor};`,
      `--primary-foreground: ${fg};`,
      `--ring: ${theme.primaryColor};`,
    );
  }
  if (theme.accentColor) {
    both.push(
      `--accent: ${theme.accentColor};`,
      `--accent-foreground: ${contrastForeground(theme.accentColor)};`,
    );
  }
  if (theme.sidebarColor) {
    const fg = contrastForeground(theme.sidebarColor);
    const hover = mix(theme.sidebarColor, fg, 0.14);
    both.push(
      `--sidebar: ${theme.sidebarColor};`,
      `--sidebar-foreground: ${fg};`,
      `--sidebar-primary: ${fg};`,
      `--sidebar-primary-foreground: ${theme.sidebarColor};`,
      `--sidebar-accent: ${hover};`,
      `--sidebar-accent-foreground: ${fg};`,
    );
  }
  if (theme.fontBody) {
    const stack = `"${theme.fontBody}", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
    both.push(`--font-sans: ${stack};`);
    bodyRules.push(`body { font-family: ${stack}; }`);
  }
  if (theme.fontDisplay) {
    const stack = `"${theme.fontDisplay}", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
    both.push(`--font-display: ${stack};`);
    displayRules.push(`.font-display { font-family: ${stack}; }`);
  }

  const varsBlock = both.length > 0 ? `:root, .dark { ${both.join(" ")} }` : "";
  return [varsBlock, ...bodyRules, ...displayRules].filter(Boolean).join("\n");
}

const FONT_LINK_ID = "theme-override-fonts";

/**
 * Applies the admin-configured workspace theme (Settings → Theme) app-wide:
 * injects CSS-variable overrides and loads any configured Google Fonts.
 * Mounted once at the root; renders nothing until the theme query resolves,
 * and nothing at all when no overrides are set.
 */
export function ThemeOverrides() {
  const { data: theme } = useThemeSettings();

  // Load configured fonts from Google Fonts. Family names are validated
  // server-side to letters/digits/spaces, so the URL interpolation is safe.
  const families = [
    ...new Set([theme?.fontBody, theme?.fontDisplay].filter((f): f is string => Boolean(f))),
  ];
  const fontHref =
    families.length > 0
      ? `https://fonts.googleapis.com/css2?${families
          .map((f) => `family=${f.replaceAll(" ", "+")}:wght@400;500;600;700`)
          .join("&")}&display=swap`
      : null;

  useEffect(() => {
    const existing = document.getElementById(FONT_LINK_ID);
    if (!fontHref) {
      existing?.remove();
      return;
    }
    if (existing instanceof HTMLLinkElement) {
      if (existing.href !== fontHref) existing.href = fontHref;
      return;
    }
    const link = document.createElement("link");
    link.id = FONT_LINK_ID;
    link.rel = "stylesheet";
    link.href = fontHref;
    document.head.appendChild(link);
  }, [fontHref]);

  if (!theme) return null;
  const css = buildOverrideCss(theme);
  if (!css) return null;
  return <style>{css}</style>;
}
